import { Title } from "@mantine/core";
import { createContext, ReactNode, useContext, useState } from "react";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { useOnNavAway } from "shared/hooks/useOnNavAway";
import { some } from "shared/utils/maybe";
import { Drawer as Vaul } from "vaul";

type Presentable =
    | ReactNode
    | { label: ReactNode; didDismiss?: () => unknown; component: ReactNode; sticky?: boolean; mode?: "aside" | "drawer" };

export const AsideCtx = createContext<{
    sticky?: boolean;
    present: (presentable: Presentable) => void;
    toggle: (presentable: Presentable) => void;
    dismiss: (opts?: { silent?: boolean }) => void;
    content: ReactNode;
    label: ReactNode;
    mode: "aside" | "drawer";
} | null>(null);

export const AsideProvider = ({ children }: { children: ReactNode }) => {
    const [label, setLabel] = useState<ReactNode>("");
    const [content, setContent] = useState<ReactNode>(null);
    const [sticky, setSticky] = useState(false);
    const [mode, setMode] = useState<"aside" | "drawer">("aside");
    const [dismissListener, setDismissListener] = useState<() => unknown>();

    function present(args: Presentable) {
        if (!!args && typeof args == "object" && "component" in args) {
            setSticky(!!args.sticky);
            setLabel(args.label);
            setContent(args.component);
            setDismissListener(() => args.didDismiss);
            setMode(args.mode ?? "aside");
        } else {
            dismiss();
        }
    }

    function toggle(args: Presentable) {
        if (!!content) dismiss();
        else present(args);
    }

    function dismiss(opts?: { silent?: boolean }) {
        if (!opts?.silent) dismissListener?.();
        setDismissListener(undefined);

        setLabel("");
        setContent(null);
    }

    useOnNavAway(() => {
        if (sticky) return;
        dismiss();
    }, []);

    return (
        <AsideCtx.Provider value={{ present, dismiss, content, label, toggle, mode }}>
            <div data-vaul-drawer-wrapper>
                <Vaul.Root open={!!content} onClose={dismiss} onOpenChange={(open) => !open && dismiss()} shouldScaleBackground>
                    <MobileAsideSheet />
                    {children}
                </Vaul.Root>
            </div>
        </AsideCtx.Provider>
    );
};

function MobileAsideSheet() {
    const isMobile = useIsMobile();
    const aside = useAside();

    if (!isMobile) return null;
    return (
        <Vaul.Portal>
            <Vaul.Overlay
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                    zIndex: "calc(var(--notifications-z-index) - 2)",
                }}
            />
            <Vaul.Content
                style={{
                    backgroundColor: "var(--mantine-color-default)",
                    display: "flex",
                    flexDirection: "column",
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                    marginTop: "6px",
                    height: "97dvh",
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    outline: "none",
                    zIndex: "calc(var(--notifications-z-index) - 1)",
                }}
            >
                <div
                    style={{
                        padding: "1rem",
                        borderTopLeftRadius: "10px",
                        flex: "1",
                        overflowY: "auto",
                    }}
                >
                    <div
                        aria-hidden
                        style={{
                            marginLeft: "auto",
                            marginRight: "auto",
                            width: "3rem",
                            height: "0.375rem",
                            flexShrink: 0,
                            borderRadius: "9999px",
                            backgroundColor: "#d1d5db",
                            marginBottom: "2rem",
                        }}
                    />
                    <div
                        style={{
                            maxWidth: "28rem",
                            marginLeft: "auto",
                            marginRight: "auto",
                        }}
                    >
                        {some(aside.label)
                            .if((it) => it != "")
                            ?.take((it) => (
                                <Title order={3} mb="md">
                                    {it}
                                </Title>
                            ))}
                        <div
                            style={{
                                fontWeight: 500,
                                marginBottom: "1rem",
                            }}
                        >
                            {aside.content}
                        </div>
                    </div>
                </div>
            </Vaul.Content>
        </Vaul.Portal>
    );
}

export function useAside() {
    const { content, ...rest } = useContext(AsideCtx)!;

    return { isPresenting: !!content, content, ...rest };
}
